import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react';
import {
  isOnlyRCFOffer,
  isRCFOffer,
  Offer,
  ProductType,
  getProductType,
} from 'components/SummaryCard/SummaryCard.helpers';
import { useLocale } from 'hooks/useLocale';
import { TIntroducerApplication } from 'lendingApi/applications.types';
import { useGetIntroducerApplication } from 'lendingApi/lendingApiHooks';
import {
  APPLICATION_STATUS,
  PRODUCT_SELECTOR_STATUSES,
} from 'utils/applications/applications.config';

export const ReviewOfferContext = createContext<
  | {
      hasMultipleOffers: boolean;
      isSingleOffer: boolean;
      isLoading: boolean;
      isRCFView: boolean;
      hasBothProductTypes: boolean;
      hasInterestOnlyOffer: boolean;
      selectedOfferId: string;
      offerDecision: Offer;
      selectedApplication: TIntroducerApplication;
      selectedOfferMonthlyRepayment: number;
      activeProductType: ProductType | null;
      showProductSelector: boolean;
      setSelectedOfferId: Dispatch<SetStateAction<string>>;
      setHasInterestOnlyOffer: Dispatch<SetStateAction<boolean>>;
      setOfferDecision: Dispatch<SetStateAction<Offer>>;
      setSelectedOfferMonthlyRepayment: Dispatch<SetStateAction<number>>;
      setSelectedProductType: Dispatch<SetStateAction<ProductType | null>>;
    }
  | undefined
>(undefined);

export const ReviewOfferProvider = (props: any) => {
  const [selectedOfferId, setSelectedOfferId] = useState<string>('');
  const [offerDecision, setOfferDecision] = useState<Offer>(Offer.UNDECIDED);
  const [hasInterestOnlyOffer, setHasInterestOnlyOffer] = useState(false);
  const [selectedOfferMonthlyRepayment, setSelectedOfferMonthlyRepayment] =
    useState<number>();
  const [selectedProductType, setSelectedProductType] =
    useState<ProductType | null>(null);

  const { isUK, isDE } = useLocale();
  const { application, isLoading } = useGetIntroducerApplication();

  const formalOffers =
    (application && application?.formal_offer_sets?.[0]?.formal_offers) ?? [];

  const hasMultipleOffers = formalOffers.length > 1;

  const isSingleOffer = formalOffers.length === 1;

  const hasBothProductTypes =
    formalOffers.some((o) => isRCFOffer(o, isDE)) &&
    formalOffers.some((o) => !isRCFOffer(o, isDE));

  const showProductSelector = Boolean(
    hasBothProductTypes &&
    application &&
    PRODUCT_SELECTOR_STATUSES.includes(application.status),
  );

  const activeProductType = getProductType(
    selectedProductType,
    showProductSelector,
    hasBothProductTypes,
    formalOffers,
    isDE,
  );

  const isRCFView =
    isOnlyRCFOffer(formalOffers, isDE) ||
    (hasBothProductTypes && activeProductType === ProductType.RCF);

  const showInterestOnly =
    application &&
    [
      APPLICATION_STATUS.offered,
      APPLICATION_STATUS.conditional_offer,
      APPLICATION_STATUS.funded,
      APPLICATION_STATUS.talking_to_customer,
    ].includes(application.status);

  return (
    <ReviewOfferContext.Provider
      value={{
        hasMultipleOffers,
        isRCFView,
        hasBothProductTypes,
        hasInterestOnlyOffer: hasInterestOnlyOffer && showInterestOnly && isUK,
        isSingleOffer,
        isLoading,
        selectedOfferId,
        offerDecision,
        selectedApplication: application,
        selectedOfferMonthlyRepayment,
        activeProductType,
        showProductSelector,
        setSelectedOfferId,
        setHasInterestOnlyOffer,
        setOfferDecision,
        setSelectedOfferMonthlyRepayment,
        setSelectedProductType,
      }}
      {...props}
    />
  );
};

export function useReviewOffer() {
  const context = useContext(ReviewOfferContext);
  if (context === undefined) {
    throw new Error('useReviewOffer must be used within');
  }
  return context;
}
